/**
 * FeedbackEmojis — Smiley/frown feedback buttons in cloud bubbles
 *
 * Matches Figma feedback_icons: emoji faces inside cloud-shaped containers.
 * Replaces the old FeedbackHeart component.
 *
 * @see Figma_assets/iOS/feedback_icons@2x.png
 */

import { cn } from '@ownyou/ui-design-system';

export interface FeedbackEmojisProps {
  state?: 'none' | 'positive' | 'negative';
  onPositive?: () => void;
  onNegative?: () => void;
  size?: 'sm' | 'default';
  className?: string;
}

function CloudBubble({
  children,
  active,
  onClick,
  label,
  className,
  ...rest
}: {
  children: React.ReactNode;
  active: boolean;
  onClick?: () => void;
  label: string;
  className?: string;
} & Record<string, unknown>) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      aria-label={label}
      className={cn(
        'relative flex items-center justify-center',
        'transition-all duration-200',
        active ? 'scale-125' : 'hover:scale-110 hover:opacity-80',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export function FeedbackEmojis({
  state = 'none',
  onPositive,
  onNegative,
  size = 'default',
  className,
}: FeedbackEmojisProps) {
  const emojiSize = size === 'sm' ? 'w-8 h-8' : 'w-10 h-10';
  const fontSize = size === 'sm' ? 'text-base' : 'text-xl';

  return (
    <div
      data-testid="feedback-emojis"
      className={cn('flex items-center gap-2', className)}
    >
      <CloudBubble
        active={state === 'positive'}
        onClick={onPositive}
        label="I like this"
        className={emojiSize}
        data-action="emoji-positive"
      >
        <span className={cn(fontSize, 'select-none')} aria-hidden="true">
          {state === 'positive' ? '😊' : '🙂'}
        </span>
      </CloudBubble>

      <CloudBubble
        active={state === 'negative'}
        onClick={onNegative}
        label="Not for me"
        className={emojiSize}
        data-action="emoji-negative"
      >
        <span className={cn(fontSize, 'select-none')} aria-hidden="true">
          {state === 'negative' ? '😠' : '🙁'}
        </span>
      </CloudBubble>
    </div>
  );
}
